// src/components/charts/DoughnutTypeBat.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../services/api';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions,
  Plugin,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend);

type Item = { libTypbat: string; total: number };
type ApiRes = {
  period: { start: string; end: string; type: string; annee?: number; mois?: number | null };
  total: number;
  items: Item[];
};

type Props = {
  date?: string;
  annee?: number;
  mois?: number;
  maxSlices?: number;
  className?: string;
  title?: string;
};

// Palette étendue pour limiter les doublons
const PALETTE = [
  '#4f46e5','#06b6d4','#22c55e','#f59e0b','#ef4444','#a855f7','#14b8a6','#3b82f6',
  '#e11d48','#84cc16','#0ea5e9','#f97316','#64748b','#16a34a','#8b5cf6','#f43f5e',
  '#10b981','#2563eb','#d946ef','#059669','#7c3aed','#facc15','#ea580c','#0ea5e9',
];

function fmt(n: number) {
  return new Intl.NumberFormat('fr-FR').format(n || 0);
}

// Texte centré au milieu du donut
const centerTextPlugin = (getText: () => string): Plugin<'doughnut'> => ({
  id: 'centerText',
  afterDraw: (chart) => {
    const { ctx, chartArea } = chart;
    if (!ctx || !chartArea) return;
    const txt = getText();
    ctx.save();
    ctx.font = '600 14px system-ui, sans-serif';
    ctx.fillStyle = '#111827';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(txt, (chartArea.left + chartArea.right) / 2, (chartArea.top + chartArea.bottom) / 2);
    ctx.restore();
  }
});

export default function DoughnutTypeBat({
  date,
  annee,
  mois,
  maxSlices = 10,
  className = '',
  title = 'Répartition par type de bateau',
}: Props) {
  const [dataApi, setDataApi] = useState<ApiRes | null>(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  // --- Chargement API ---
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setErr('');

        const params: any =  {};
        if (date) params.date = date;
        else if (annee && mois) { params.annee = annee; params.mois = mois; }
        else if (annee) params.annee = annee;

        const res = await api.get<ApiRes>('/type-bat', { params });
        if (!cancelled) setDataApi(res.data);
      } catch (e: any) {
        if (!cancelled) setErr(e?.response?.data?.error || 'Erreur de chargement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [date, annee, mois]);

  // --- Préparation des données du donut ---
  const { chartData, totalAll } = useMemo(() => {
    if (!dataApi) return { chartData: { labels: [], datasets: [] }, totalAll: 0 };

    const items = [...dataApi.items].sort((a,b) => b.total - a.total);
    const top = items.slice(0, maxSlices);
    const rest = items.slice(maxSlices);

    const labels = top.map(it => it.libTypbat);
    const data = top.map(it => it.total);
    const sumTop = data.reduce((s,v)=>s+v,0);
    const sumRest = rest.reduce((s,v)=>s+v.total,0);

    if (sumRest>0) {
      labels.push('Autres');
      data.push(sumRest);
    }

    const colors = labels.map((_,i)=>PALETTE[i % PALETTE.length]);

    return {
      chartData: {
        labels,
        datasets: [{ data, backgroundColor: colors, borderWidth: 1.5, hoverOffset: 6 }]
      },
      totalAll: sumTop+sumRest
    };
  }, [dataApi, maxSlices]);

  // --- Options du donut ---
  const options: ChartOptions<'doughnut'> = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    cutout: '55%',
    plugins: {
      legend: { position: 'right', labels: { usePointStyle: true } },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const label = ctx.label || '';
            const val = typeof ctx.parsed === 'number' ? ctx.parsed : 0;
            const total = (ctx.dataset.data as number[]).reduce((s,n)=>s+(n||0),0);
            const pct = total? Math.round((val/total)*100):0;
            return `${label}: ${fmt(val)} (${pct}%)`;
          }
        }
      }
    }
  }), []);

  // --- Rendu ---
  return (
    <div className={`rounded-2xl border border-gray-200 bg-white px-5 pb-4 pt-5 dark:border-darkBorderCard dark:bg-white/[0.03] ${className}`}>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white">{title}</h3>
        {dataApi?.period && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Période : {dataApi.period.start} → {dataApi.period.end}
          </p>
        )}
      </div>

      {loading && <div className="text-gray-500">Chargement…</div>}
      {err && !loading && <div className="text-red-600">{err}</div>}

      {!loading && !err && chartData.labels?.length ? (
        <div style={{height:340}}>
          <Doughnut data={chartData} options={options} plugins={[centerTextPlugin(()=>`Total\n${fmt(totalAll)}`)]}/>
        </div>
      ): null}
    </div>
  );
}
